/**
 * 9-week dual-axis chart for the StudentDrillPanel.
 *
 * Per spec §6.2:
 *   - Bars (purple, light): weekly question volume (effort)
 *   - Line (green): weekly accuracy %  (quality)
 *   - Final accuracy value labelled at the rightmost data point
 *   - Week labels W1–W9 on x-axis
 *   - NO y-axis values shown — relative trend is what matters, not absolute
 *
 * Data: weekly_history from getStudentDrillDownV2 — array of
 *   { week_label, week_iso, questions, accuracy_pct }.
 *
 * Empty state: per plan §4.7, when student has zero weekly_buckets
 * for this subject the chart's parent component renders the
 * "Not enough practice history yet" message instead.
 */

import {
  ComposedChart, Bar, Line, XAxis, YAxis, ResponsiveContainer,
  Tooltip, LabelList,
} from 'recharts';

const PURPLE_LIGHT = '#EDE9FE';
const GREEN = '#059669';

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const q = payload.find(p => p.dataKey === 'questions');
  const a = payload.find(p => p.dataKey === 'accuracy_pct');
  return (
    <div className="bg-white border border-teacher-border rounded-md shadow-sm px-2.5 py-1.5 text-[11px]">
      <div className="font-medium text-teacher-text-primary">{label}</div>
      {q && <div className="text-teacher-text-secondary">{q.value} questions</div>}
      {a && <div className="text-teacher-green">{a.value}% accuracy</div>}
    </div>
  );
}

export default function WeeklyProgressChart({ data }) {
  // Render the final accuracy label only on the last data point
  const lastIndex = (data?.length || 0) - 1;
  const dataWithLabel = (data || []).map((d, i) => ({
    ...d,
    final_acc_label: i === lastIndex ? `${d.accuracy_pct}%` : '',
  }));

  return (
    <div className="bg-white border border-teacher-border rounded-xl px-4 py-3.5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12px] uppercase tracking-wider font-medium text-teacher-text-secondary">
          Progress over 9 weeks
        </div>
        <div className="flex gap-3.5 text-[11px] text-teacher-text-secondary">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-2 rounded-sm" style={{ background: PURPLE_LIGHT }} />
            Questions / week
          </div>
          <div className="flex items-center gap-1.5 relative">
            <div className="w-4 h-0.5" style={{ background: GREEN }} />
            <div className="w-1.5 h-1.5 rounded-full absolute right-0 -top-0.5" style={{ background: GREEN }} />
            <span className="ml-1.5">Accuracy %</span>
          </div>
        </div>
      </div>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={dataWithLabel} margin={{ top: 10, right: 18, left: 0, bottom: 4 }}>
            <XAxis
              dataKey="week_label"
              tick={{ fontSize: 10, fill: '#9E9B94' }}
              tickLine={false}
              axisLine={false}
            />
            {/* Hidden y-axes — spec is explicit: no y-axis values shown. */}
            <YAxis yAxisId="q" hide />
            <YAxis yAxisId="a" orientation="right" domain={[0, 100]} hide />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.02)' }} />
            <Bar yAxisId="q" dataKey="questions" fill={PURPLE_LIGHT} radius={[3, 3, 0, 0]} />
            <Line
              yAxisId="a"
              type="linear"
              dataKey="accuracy_pct"
              stroke={GREEN}
              strokeWidth={2}
              dot={{ r: 3, fill: GREEN }}
              activeDot={{ r: 4 }}
            >
              <LabelList
                dataKey="final_acc_label"
                position="top"
                fill={GREEN}
                fontSize={10}
              />
            </Line>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
